import React from 'react';
import { motion } from 'motion/react';
import { BarChart3, ShieldCheck, Search, Eye, FileText, ClipboardCheck, History, Radio } from 'lucide-react';

export default function LoggingMonitoringPolicy() {
  const lastUpdated = "April 21, 2026";

  const monitoringPillars = [
    {
      title: "Audit Logging",
      description: "Every key system interaction is timestamped and recorded in immutable audit logs, providing a definitive record for accountability and forensic investigation.",
      icon: FileText
    },
    {
      title: "Anomaly Detection",
      description: "Our systems utilize behavioral baselining to identify deviations and anomalies. This allows us to spot suspicious activity that might bypass traditional rules.",
      icon: Eye
    },
    {
      title: "Access Tracking",
      description: "Unauthorized access attempts and privilege escalations are tracked in real-time. Every failed authentication triggers an immediate security alert for investigation.",
      icon: Radio
    },
    {
      title: "Periodic Reviews",
      description: "Beyond automated alerts, our security team conducts periodic deep-dive reviews of all system logs to identify long-term patterns or subtle persistences.",
      icon: ClipboardCheck
    }
  ];

  return (
    <div className="min-h-screen bg-background pt-32 pb-20">
      <div className="container mx-auto px-4 md:px-8">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-4xl mx-auto space-y-12"
        >
          {/* Header */}
          <div className="space-y-4 text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/5 border border-accent/10 text-accent text-[10px] font-bold uppercase tracking-[0.3em]">
              Continuous Visibility
            </div>
            <h1 className="text-4xl md:text-6xl font-display font-bold tracking-tight">
              Logging & <span className="text-accent">Monitoring</span>
            </h1>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Agrandintl maintains rigorous logging and monitoring controls to ensure total transparency, accountability, and rapid threat detection.
            </p>
          </div>

          {/* Visibility Dashboard Visual */}
          <div className="glass-card p-10 rounded-[2.5rem] border-white/5 bg-white/[0.01] relative overflow-hidden text-center group">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-accent/30 to-transparent" />
            <div className="space-y-6 relative z-10">
              <div className="w-16 h-16 rounded-3xl bg-accent/10 flex items-center justify-center text-accent mx-auto mb-4 border border-accent/20">
                <BarChart3 className="w-8 h-8" />
              </div>
              <h2 className="text-2xl font-display font-bold">The Observability Advantage</h2>
              <p className="text-muted-foreground leading-relaxed text-base max-w-2xl mx-auto italic">
                "We operate on the principle that you cannot protect what you cannot see. Our monitoring infrastructure provides the deep visibility required to neutralize threats before they can impact business continuity."
              </p>
            </div>
          </div>

          {/* Pillars Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {monitoringPillars.map((pillar, idx) => (
              <motion.div
                key={pillar.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                className="glass-card p-8 rounded-3xl border-white/5 bg-white/[0.01] hover:border-accent/10 transition-all group"
              >
                <div className="flex items-start gap-5">
                  <div className="w-12 h-12 rounded-2xl bg-white/5 flex items-center justify-center text-accent shrink-0 group-hover:bg-accent group-hover:text-background transition-all duration-300">
                    <pillar.icon className="w-6 h-6" />
                  </div>
                  <div className="space-y-2">
                    <h3 className="text-lg font-display font-bold">{pillar.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed group-hover:text-foreground transition-colors">
                      {pillar.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Policy Invariants */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { icon: History, label: "Retention", value: "365 Days" },
              { icon: ShieldCheck, label: "Integrity", value: "WORM Storage" },
              { icon: Search, label: "Search", value: "Real-time" }
            ].map((item) => (
              <div key={item.label} className="p-6 rounded-2xl bg-white/[0.02] border border-white/5 flex items-center gap-4">
                <div className="w-8 h-8 rounded-lg bg-accent/5 flex items-center justify-center text-accent">
                  <item.icon className="w-4 h-4" />
                </div>
                <div>
                  <div className="text-[8px] uppercase font-bold tracking-widest text-muted-foreground">{item.label}</div>
                  <div className="text-xs font-bold">{item.value}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Documentation Meta */}
          <div className="pt-12 border-t border-white/5 text-center">
            <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-muted-foreground/60 mb-2">
              Policy ID: AG-LO-21 | Security Classification: Public
            </div>
            <div className="text-[10px] text-muted-foreground/40 font-medium italic">
              Last Verified & Updated: {lastUpdated}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
